import React from 'react';
import { Eye, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { errMsg } from '@/services/staffAttendanceApi';
import { ewbPdf } from '@/services/ewayBillApi';
import { fmtDT, ModeBadge, PartBPending, PrintedMark, StatusBadge, ValidLeft } from '@/components/ewayBill/shared';

const HEADS = ['EWB No', 'Created', 'Type', 'Document', 'Party', 'Vehicle', 'Km', 'Valid until', 'Status', ''];
const WAITING = ['AWAITING', 'QUEUED', 'IN_TALLY', 'TALLY_ERROR'];
export const isWaiting = (b) => WAITING.includes(b.status) && !b.ewb_no;
export const BILL_TABS = [
  { id: 'waiting', label: 'Waiting for e-way Bill', has: isWaiting, empty: 'No bills waiting for an e-way bill in this range' },
  { id: 'active', label: 'Active', has: (b) => b.status !== 'CANCELLED' && !isWaiting(b), empty: 'No active or expired e-way bills in this range' },
  { id: 'discarded', label: 'Discarded', has: (b) => b.status === 'CANCELLED', empty: 'No discarded or cancelled e-way bills in this range' },
];

/** Outward bills show the receiver; inward bills the sender with an IN tag. */
const PartyCell = ({ b }) => {
  const inward = b.supply_type === 'Inward';
  const p = inward ? b.from : b.to;
  return (
    <TableCell className="max-w-[170px] truncate" title={[p?.trade_name || p?.legal_name, p?.place].filter(Boolean).join(' · ')}>
      {inward && <span className="mr-1 rounded bg-violet-100 px-1 text-[10px] font-bold text-violet-800" title="Inward — goods we receive" data-testid={`ewb-inward-tag-${b.id}`}>IN</span>}
      <span data-testid={`ewb-party-${b.id}`}>{p?.trade_name || p?.legal_name}</span><div className="text-xs text-gray-500">{p?.place}</div>
    </TableCell>
  );
};

/** All e-way bills of the site — click a row for details, PDF straight from the row; tick waiting bills for the portal JSON. */
export const EwbTable = ({ rows, onOpen, picked, onPick, onPrinted, canPick = isWaiting, empty = 'No e-way bills in this range' }) => {
  const pdf = (e, b) => { e.stopPropagation(); ewbPdf(b).then(() => onPrinted?.()).catch((x) => toast.error(errMsg(x, 'PDF failed'))); };
  if (!rows.length) {
    return <div className="rounded-xl bg-white shadow-sm py-14 text-center text-gray-500" data-testid="ewb-empty">{empty}</div>;
  }
  return (
    <div className="rounded-xl bg-white shadow-sm overflow-x-auto">
      <Table data-testid="ewb-table">
        <TableHeader><TableRow>{onPick && <TableHead className="w-8" />}{HEADS.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.map((b) => (
            <TableRow key={b.id} className="cursor-pointer hover:bg-slate-50" onClick={() => onOpen(b)} data-testid={`ewb-row-${b.id}`}>
              {onPick && (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {canPick(b) && <Checkbox checked={picked.has(b.id)} onCheckedChange={() => onPick(b.id)} data-testid={`ewb-pick-${b.id}`} />}
                </TableCell>
              )}
              <TableCell className="font-mono font-semibold whitespace-nowrap" data-testid={`ewb-no-${b.id}`}>
                {b.ewb_no || <span className="text-gray-400">—</span>} {['mock', 'sandbox'].includes(b.mode) && <ModeBadge mode={b.mode} />}
              </TableCell>
              <TableCell className="text-xs min-w-[92px]">{fmtDT(b.created_at)}</TableCell>
              <TableCell className="text-xs">{b.doc_type_label}</TableCell>
              <TableCell className="font-mono text-xs">{b.doc_no}</TableCell>
              <PartyCell b={b} />
              <TableCell className="font-mono text-xs">{b.part_b?.vehicle_no || '—'}<PartBPending b={b} testId={`ewb-partb-pending-${b.id}`} /></TableCell>
              <TableCell className="tabular-nums">{b.part_b?.distance_km}</TableCell>
              <TableCell className="text-xs min-w-[92px]" data-testid={`ewb-valid-${b.id}`}>{fmtDT(b.valid_upto)}<ValidLeft b={b} /></TableCell>
              <TableCell>
                <StatusBadge b={b} testId={`ewb-status-${b.id}`} />
                <PrintedMark b={b} testId={`ewb-printed-${b.id}`} />
                {isWaiting(b) && b.portal_json && <div className="mt-0.5 text-[11px] text-gray-500" data-testid={`ewb-json-at-${b.id}`}>JSON {fmtDT(b.portal_json.downloaded_at)}</div>}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {b.ewb_no && <Button size="sm" variant="ghost" onClick={(e) => pdf(e, b)} data-testid={`ewb-pdf-${b.id}`}><FileDown className="w-4 h-4" /></Button>}
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpen(b); }} data-testid={`ewb-view-${b.id}`}><Eye className="w-4 h-4" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default EwbTable;
